import { useState } from 'react';
import { FilterBar } from './filter-bar';
import type { FilterChip } from './filter-chips';
import { attachFieldControls, FilterFieldStack, type FilterFieldControl } from './filter-field';
import { countRemovableChips } from './filter-schema';
import { FilterShell } from './filter-shell';
import { Toolbar, type ToolbarAction } from './toolbar';

export interface FilterSectionProps {
  /** Applied 투영 칩(스키마 산출물). 컨트롤 결합은 여기서 한다 — 호출처는 투영만 넘긴다. */
  chips: FilterChip[];
  /** 드로워 본문에 세로로 쌓을 차원별 컨트롤. */
  fields: FilterFieldControl[];
  /**
   * **칩 팝오버 전용** 컨트롤(기본 `fields`) — staged 필터에서만 갈린다. 드로워는 draft 를 쓰고([조회]로 커밋),
   * 칩은 Applied 를 직접 쓰기 때문(칩은 Applied 의 표면이라 편집도 즉시가 맞다). live 필터는 둘이 같은 한 벌.
   */
  chipFields?: FilterFieldControl[];
  /** 페이지 액션(다운로드·갱신 등) — 트레일링 `Toolbar`. */
  actions?: ToolbarAction[];
  /** staged 커밋([조회]) — 생략 시 live([닫기]). */
  onApply?: () => boolean | void;
  canApply?: boolean;
  /** 초기화 — 칩 행 끝 + 드로워 푸터 공용. */
  onReset?: () => void;
  /** 초기화 노출 조건(기본 true — 화면이 isDirty 주입). */
  canReset?: boolean;
  /**
   * **편집 표면이 닫힐 때 임시 상태 폐기**(선택) — 드로워·칩 팝오버 공용. staged 화면이 넘긴다: 조회 없이 닫은
   * draft 는 버려야 다음에 열었을 때 Applied(=화면에 보이는 조건)에서 다시 시작한다. 조회 후 닫힐 때도 불리지만
   * 그땐 Applied 가 곧 draft 라 무해하다(재시드=동일값).
   */
  onDiscard?: () => void;
  /** 드로워 제목(기본 조회조건). */
  title?: string;
  /**
   * 열림 상태(선택) — **제어형**. 화면 밖에도 트리거가 있을 때만 준다(예: 발전량 위젯의 "조회조건 열기" 링크).
   * 생략하면 이 컴포넌트가 상태를 소유한다(칩·[조회조건] 버튼만으로 충분한 일반 경우).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * **c영역 조회조건 한 벌** — 칩 행(`FilterBar` + 트레일링 `Toolbar`)과 조회 드로워(`FilterShell`)를 함께 소유한다.
 * 네 화면(알림·발전량·수익·인버터 상세분석)이 같은 20여 줄을 각자 쓰던 것을 흡수했다: 컨트롤 결합
 * (`attachFieldControls`)·배지 계산(`countRemovableChips`)·드로워 열림 상태·본문 스택이 전부 여기 한 곳이다.
 *
 * 화면이 남기는 건 **도메인뿐** — 칩 투영(스키마)·컨트롤 목록·액션·적용 모드. 열림 상태는 이 컴포넌트 내부라
 * 화면에서 `useState` 가 사라진다(트리거는 셋 다 여기 안에 있다: 칩·[조회조건] 버튼·드로워).
 */
export function FilterSection({
  chips,
  fields,
  chipFields,
  actions,
  onApply,
  canApply,
  onReset,
  canReset = true,
  onDiscard,
  title,
  open,
  onOpenChange,
}: FilterSectionProps) {
  const [selfOpen, setSelfOpen] = useState(false);
  const isOpen = open ?? selfOpen;
  const setOpen = onOpenChange ?? setSelfOpen;
  const withControls = attachFieldControls(chips, chipFields ?? fields);
  const reset = onReset && canReset ? onReset : undefined;

  return (
    <>
      {/* 칩 클릭 = 그 차원만 편집 — pc 는 팝오버, non-pc 는 조회조건 드로워로 폴백. 초기화는 칩 행 끝. */}
      <FilterBar
        chips={withControls}
        onOpenFilter={() => setOpen(true)}
        onDiscard={onDiscard}
        onReset={reset}
      >
        <Toolbar
          filter={{ activeCount: countRemovableChips(withControls), onOpen: () => setOpen(true) }}
          actions={actions}
        />
      </FilterBar>

      <FilterShell
        open={isOpen}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) onDiscard?.();
        }}
        onApply={onApply}
        canApply={canApply}
        onReset={onReset}
        canReset={canReset}
        title={title}
      >
        <FilterFieldStack fields={fields} />
      </FilterShell>
    </>
  );
}
